import { faCheck, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

import Badge from '@/components/Badge';
import Button from '@/components/Button';
import IconicButton from '@/components/IconicButton';
import Stack from '@/components/Stack';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import { TableRow, TableCell } from '@/elements/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { formatDateSimple } from '@/util/datetime';
import ImagePreviewModal from '../shared/ImagePreviewModal';
import RejectModal from '../shared/RejectModal';
import { WorkerCertificatePhoto } from './api';

type Props = {
  photo: WorkerCertificatePhoto;
  onVerify: (id: number) => Promise<void>;
  onReject: (id: number, comment?: string) => Promise<void>;
  isVerifying?: boolean;
  isRejecting?: boolean;
  onActionComplete?: () => void;
};

const CertificateRowItem = ({
  photo,
  onVerify,
  onReject,
  isVerifying = false,
  isRejecting = false,
  onActionComplete,
}: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const isPending = photo.status === 'pending';
  const workerName = photo.worker
    ? `${photo.worker.user.first_name} ${photo.worker.user.last_name}`
    : 'Unknown Worker';
  const workerEmail = photo.worker?.user.email || '';
  const skillName =
    photo.skill?.display_name_en || photo.skill?.name || 'Unknown Skill';
  const certificateType = photo.type || 'Certificate';

  const handleVerify = async () => {
    try {
      await onVerify(photo.id);
      onActionComplete?.();
    } catch (error) {
      console.error('Failed to verify certificate:', error);
    }
  };

  const handleReject = async (comment?: string) => {
    try {
      await onReject(photo.id, comment);
      setShowRejectModal(false);
      onActionComplete?.();
    } catch (error) {
      console.error('Failed to reject certificate:', error);
      throw error;
    }
  };

  const getStatusBadge = () => {
    switch (photo.status) {
      case 'verified':
        return <Badge label="Verified" status="theme" size="sm" />;
      case 'rejected':
        return <Badge label="Rejected" status="danger" size="sm" />;
      default:
        return <Badge label="Pending" status="warning" size="sm" />;
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <a
            href={`/workers_v2/${photo.worker_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <TextStack spacing="tight">
              <Body
                weight="medium"
                style={{ color: '#2563EB', textDecoration: 'underline' }}
              >
                {workerName} #{photo.worker_id}
              </Body>
              <Small>
                {workerEmail.length > 12
                  ? `${workerEmail.substring(0, 12)}...`
                  : workerEmail}
              </Small>
            </TextStack>
          </a>
        </TableCell>
        <TableCell>
          {photo.image_url ? (
            <Stack gap={8} align="center">
              <img
                src={photo.image_url}
                alt={`${certificateType} preview`}
                style={{
                  width: '60px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowImageModal(true);
                }}
              />
              <IconicButton
                a11yLabel="View full image"
                appearance="plain"
                icon={faEye}
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowImageModal(true);
                }}
              />
            </Stack>
          ) : (
            <Small color="light">No image</Small>
          )}
        </TableCell>
        <TableCell>
          <Body>{certificateType}</Body>
        </TableCell>
        <TableCell>
          <Body>{skillName}</Body>
        </TableCell>
        <TableCell>
          {photo.expires_at ? (
            <Small
              style={{
                color:
                  new Date(photo.expires_at) < new Date()
                    ? '#DC2626'
                    : undefined,
              }}
            >
              {formatDateSimple(photo.expires_at)}
              {new Date(photo.expires_at) < new Date() && ' (Expired)'}
            </Small>
          ) : (
            <Small color="light">Not set</Small>
          )}
        </TableCell>
        <TableCell>
          <Small>{formatDateSimple(photo.created_at)}</Small>
        </TableCell>
        <TableCell>{getStatusBadge()}</TableCell>
        <TableCell>
          {isPending ? (
            <Stack gap={8} justify="end">
              <Button
                a11yLabel="Verify certificate"
                appearance="secondary"
                iconLeft={faCheck}
                isLoading={isVerifying}
                label={phoneOnly ? '' : 'Verify'}
                onClick={handleVerify}
              />
              <Button
                a11yLabel="Reject certificate"
                appearance="secondary"
                iconLeft={faTimes}
                isLoading={isRejecting}
                label={phoneOnly ? '' : 'Reject'}
                onClick={() => setShowRejectModal(true)}
                status="danger"
              />
            </Stack>
          ) : (
            <Small color="light">Processed</Small>
          )}
        </TableCell>
      </TableRow>
      <ImagePreviewModal
        isOpen={showImageModal}
        imageUrl={photo.image_url}
        onClose={() => setShowImageModal(false)}
        title={`${certificateType} - ${workerName}`}
      />
      {showRejectModal && (
        <RejectModal
          isOpen={showRejectModal}
          isLoading={isRejecting}
          onClose={() => setShowRejectModal(false)}
          onReject={handleReject}
          requireComment={false}
          title="Reject Certificate"
        />
      )}
    </>
  );
};

export default CertificateRowItem;
